import { useFilter } from '@chakra-ui/react/locale';
import { keepPreviousData } from '@tanstack/react-query';
import { useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';

import { UseMonolithApiQueryOptions } from '@/hooks/api/useMonolithApiQuery';
import { SimpleErrorResponse } from '@/models/Error';

import useBranches, { Branches } from './useBranches';
import { validateBranchName } from './validateBranchName';

// Some repositories have hundreds of thousands of branches (a ~21 MB response), and the combobox
// mounts a row for every item it is given, even while its menu is closed, which crashes the tab. So
// at most BRANCHES_FETCH_LIMIT branches are fetched, and the menu gets them a page at a time.
export const BRANCHES_PAGE_SIZE = 100;
// What the endpoint clamps `limit` to. Every request lists all of the repository's refs whatever the
// limit, so the most it returns is fetched at once, and paging through it costs no further requests.
export const BRANCHES_FETCH_LIMIT = 1000;
const SEARCH_DEBOUNCE_MS = 300;

const UNFILTERED_PARAMS = { limit: BRANCHES_FETCH_LIMIT };

type UseBranchSearchOptions = {
  enabled?: boolean;
  retry?: UseMonolithApiQueryOptions<Branches, SimpleErrorResponse>['retry'];
  selectedBranch: string | undefined;
};

/** Searches and pages a project's branches for a `BitkitCombobox`, which gets `comboboxProps`. */
const useBranchSearch = (appSlug: string, options: UseBranchSearchOptions) => {
  const { enabled = true, retry, selectedBranch } = options;
  // Left out rather than undefined when not given, so that the query client's default applies.
  const queryOptions = { enabled, ...(retry === undefined ? {} : { retry }) };
  const { contains } = useFilter({ sensitivity: 'base' });

  // What is typed, as the combobox reports it: untrimmed, and empty once the input changes any other way.
  const [inputText, setInputText] = useState('');
  const search = inputText.trim();
  const [debouncedSearch] = useDebounceValue(search, SEARCH_DEBOUNCE_MS);
  const [shownCount, setShownCount] = useState(BRANCHES_PAGE_SIZE);

  // The same params as the list itself starts with, so this observes that request rather than making one.
  // A caller preselecting a branch reads it too, since the listed branches move with the input.
  const unfilteredBranches = useBranches(appSlug, UNFILTERED_PARAMS, queryOptions).data?.branches;
  // When the unfiltered list holds every branch, the input narrows it down here and the endpoint is not
  // asked again. Only a repository with more branches than one response holds is searched on it, and
  // an emptied input goes back to the unfiltered list without waiting for the debounce.
  const hasEveryBranch = !!unfilteredBranches && unfilteredBranches.length < BRANCHES_FETCH_LIMIT;
  const serverSearch = hasEveryBranch || !search ? '' : debouncedSearch;

  const { data, error, isError, isFetching, isLoading, isPlaceholderData, refetch } = useBranches(
    appSlug,
    serverSearch ? { q: serverSearch, limit: BRANCHES_FETCH_LIMIT } : UNFILTERED_PARAMS,
    { ...queryOptions, placeholderData: keepPreviousData },
  );

  // `limit` is the endpoint's job; slicing only keeps the list bounded should a response ignore it.
  const branches = (data?.branches ?? []).slice(0, BRANCHES_FETCH_LIMIT);
  // The results can be for an earlier search, and the combobox shows its items as given.
  const matchingBranches = inputText ? branches.filter((branch) => contains(branch, inputText)) : branches;

  const isSearchPending = !hasEveryBranch && !!search && search !== debouncedSearch;
  const isFetchingResults = enabled && (isSearchPending || (isFetching && isPlaceholderData));
  // With nothing on screen matching while results are on their way, it is too early to say no branch does.
  const isAwaitingResults = isFetchingResults && matchingBranches.length === 0;

  // The combobox looks the selected option up in `items`, so whenever the selected branch fits the input
  // but is not on the first page, because it is further down the list, past the branches fetched or was
  // typed in, it leads the list instead, and leaves its own place so that scrolling never lists it twice.
  const isSelectedBranchPinned =
    !!selectedBranch &&
    !matchingBranches.slice(0, BRANCHES_PAGE_SIZE).includes(selectedBranch) &&
    validateBranchName(selectedBranch, false) &&
    contains(selectedBranch, inputText);
  const shownBranches = isSelectedBranchPinned
    ? [selectedBranch, ...matchingBranches.filter((branch) => branch !== selectedBranch)]
    : matchingBranches;
  const hasMoreBranches = shownBranches.length > shownCount;

  const handleSearchChange = (searchText: string) => {
    setInputText(searchText);
    setShownCount(BRANCHES_PAGE_SIZE);
  };

  return {
    comboboxProps: {
      // BitkitCombobox disables every item while loading; this is only true when no item is shown anyway.
      isLoading: isLoading || isAwaitingResults,
      items: shownBranches.slice(0, shownCount).map((branch) => ({ label: branch, value: branch })),
      onScrolledToBottom: hasMoreBranches ? () => setShownCount(shownCount + BRANCHES_PAGE_SIZE) : undefined,
      onSearchChange: handleSearchChange,
    },
    error,
    /** The branches the endpoint lists without a search, default-branch candidates first, whatever is typed. */
    firstPage: unfilteredBranches,
    /** Whether the project has no branches at all, as far as the responses so far tell. */
    hasNoBranches: !inputText && !debouncedSearch && branches.length === 0,
    isError,
    refetch,
  };
};

export default useBranchSearch;
